/**
 * NotificationsBell — header icon + popover inbox.
 *
 * Drops into the app / admin shell. Shows a red dot when there are unread
 * notifications, opens a popover listing the 30 most recent. Clicking an
 * item marks it read and navigates to `link` if one was attached.
 *
 * We use `@radix-ui/react-popover` directly instead of a shared shadcn
 * primitive because this is currently the only consumer. If another screen
 * adds a popover we can promote the pattern to `src/components/ui/popover.tsx`.
 */

import * as Popover from "@radix-ui/react-popover";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox as InboxIcon } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { useAuth } from "@/features/auth/AuthProvider";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  type InboxNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "./notificationsQueries";

export function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: items = [], isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  if (!user) return null;

  const unreadCount = items.filter((n) => !n.read).length;

  const handleItemClick = (n: InboxNotification) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) {
      // Internal links start with "/" — navigate via react-router so we
      // don't tear down the SPA.
      if (n.link.startsWith("/")) {
        navigate(n.link);
      } else {
        window.location.href = n.link;
      }
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 inline-flex min-w-[1rem] h-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-accent-foreground">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-border bg-popover text-popover-foreground shadow-xl outline-none animate-in fade-in-0 zoom-in-95"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="font-heading text-sm font-semibold">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {isLoading ? (
              <ul className="divide-y divide-border" aria-busy="true" aria-label="Loading notifications">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <Skeleton className="mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-2.5 w-20" />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-muted-foreground">
                <InboxIcon className="h-6 w-6" />
                <p className="text-sm">You're all caught up.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      className={cn(
                        "w-full text-left px-4 py-3 transition-colors hover:bg-secondary/50 focus:outline-none focus-visible:bg-secondary/50",
                        !n.read && "bg-accent/5",
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && (
                          <span
                            aria-hidden
                            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className={cn("text-sm leading-snug", !n.read && "font-semibold")}>
                            {n.title}
                          </p>
                          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                            {n.message}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground/80">
                            {formatDistanceToNowStrict(new Date(n.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
